'use client';

import { Skeleton, Spinner } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';

import { GoodsCard } from '../../../_components/GoodsCard';
import { GoodsCardSkeleton } from '../../../_components/GoodsCardSkeleton';
import { SORT_TYPE, SORT_TYPE_OPTIONS } from '../../../_constants';

interface GoodsListByBrandProps {
  className?: string;
  brandName?: string;
}

const PAGE_SIZE = 8;

export const GoodsListByBrand = ({
  className,
  brandName,
}: GoodsListByBrandProps) => {
  const [sortType, setSortType] = useState(SORT_TYPE.POPULAR);
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...UnauthInfiniteQueryOptions['get:/wello/v2/allows/point-market/product']({
      query: {
        brandName,
        page: 1,
        size: PAGE_SIZE,
        sortType,
      },
    }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.context) return;
      const { totalCount: total_count, size, page } = lastPage.context;

      const hasNextPage = total_count > size * page;

      if (hasNextPage) return page + 1;
    },
    select: (data) => {
      if (!data) return;

      const contents = data.pages.flatMap(
        (page) => page.context?.contents ?? [],
      );

      const lastPage = data.pages[data.pages.length - 1];

      return {
        totalCount: lastPage.context?.totalCount ?? 0,
        contents,
      };
    },
    initialPageParam: 1,
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (!isFetching && inView) fetchNextPage();
    },
  });

  return (
    <section className={cn('mb-24 mt-15 w-full', className)}>
      <div className="mb-8 flex h-40 items-center justify-between px-20">
        {data ? (
          <p className="font-500 text-Gray-900 typo-body4">
            전체 {data?.totalCount}개
          </p>
        ) : (
          <Skeleton className="h-21 w-57" />
        )}
        <select
          aria-label="정렬"
          className="text-Gray-900 typo-body4"
          value={sortType}
          onChange={(e) => setSortType(e.target.value as SORT_TYPE)}
        >
          {SORT_TYPE_OPTIONS.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <ul className="mx-20 mb-16 grid grid-cols-3 gap-x-12 gap-y-20">
        {data
          ? data.contents.map((item) => {
              return (
                <li key={item.productIdIdx}>
                  <GoodsCard
                    className="h-fit w-full"
                    title={item.productName}
                    thumbnailImage={item.thumbnailUrl}
                    idx={item.productIdIdx}
                    price={item.originalPrice}
                    brandName={item.brandName}
                  />
                </li>
              );
            })
          : new Array(PAGE_SIZE).fill(null).map((_, index) => (
              <li key={index}>
                <GoodsCardSkeleton className="h-fit w-full" />
              </li>
            ))}
      </ul>
      {hasNextPage ? (
        <div className="my-30 w-full flex-center" ref={ref}>
          <Spinner />
        </div>
      ) : null}
    </section>
  );
};
